import { useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { GaugeIcon } from 'lucide-react'

import { Card } from '@repo/ui'
import { EmptyState } from 'components'
import { headerWithInfo, INFO_HEADER_ALLOWANCE } from 'components/display/Table/headerWithInfo'
import { Table } from 'components/display/Table/Table'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useWebsiteVitals } from 'hooks/api'
import type { DayRange, VitalName, WebsiteVitalReading, WebsiteVitalsRow } from 'services'

import { AUDIENCE_LABELS, formatCount, sourceNotice } from '../websiteAnalyticsModel'
import { Sparkline } from './seo/seoModel'
import { Notice, SectionNote, shortDate, useTone, type Tone } from './shared'

/**
 * VRG-762 — how fast vera.inc actually is for the people who visit it.
 *
 * Real-visitor Core Web Vitals from Vercel Speed Insights: p75 LCP, INP, CLS, FCP and
 * TTFB per route and per device, with a daily trend so a change is visible as a change.
 * Built because the delivery side measured fine (TTFB 165–190ms from a warm edge,
 * `X-Vercel-Cache: HIT`) while visitors reported the homepage as slow — the weight is all
 * client-side, and nothing on this page had a number for what a real phone experiences.
 *
 * A SECTION of the page, so the shared Table runs embedded: no action line, no footer, a
 * fixed height, no `fillBottom` (§22).
 *
 * ── ONE POPULATION, AND IT IS EVERYONE ─────────────────────────────────────────────
 * Unlike every other panel here this one shows a single source and says so. Vercel Speed
 * Insights is cookieless and covers all visitors; PostHog would cover only those with
 * analytics on — opt-in outside the US — so a PostHog series would be a self-selected
 * slice measured as if it were the population. For "how slow is it for the people who
 * come here", the biased sample is the worse instrument, not a second opinion.
 *
 * ── WHY EVERY NUMBER CARRIES ITS SAMPLE COUNT ──────────────────────────────────────
 * vera.inc's traffic is small: `/` collects ~6.5 LCP datapoints a day and every other
 * route 1–43 over a whole 30 days (measured 2026-10-27). A p75 of four visits is "the
 * slowest of the four visits we saw", not a percentile, so the row prints its sample
 * count and a figure under the API's `minSamples` renders grey and parenthesised rather
 * than as a fact. Without that, the panel would confidently report `/certificates` INP at
 * 42 seconds — which was one person on one bad device.
 */

/** Google's grading bands, in each vital's own unit. Mirrors the API's VITAL_THRESHOLDS. */
const THRESHOLDS: Record<VitalName, { good: number; poor: number }> = {
  lcp: { good: 2_500, poor: 4_000 },
  inp: { good: 200, poor: 500 },
  cls: { good: 0.1, poor: 0.25 },
  fcp: { good: 1_800, poor: 3_000 },
  ttfb: { good: 800, poor: 1_800 },
}

const VITAL_LABELS: Record<VitalName, string> = {
  lcp: 'LCP',
  inp: 'INP',
  cls: 'CLS',
  fcp: 'FCP',
  ttfb: 'TTFB',
}

/*
  What each column means, as prose rather than a glossary term: these are explanations of
  THIS COLUMN'S DATA (which threshold, which unit, why a figure may be greyed), not
  definitions of a word, which is the split `headerWithInfo` exists for. Each names its
  own "good" bar so a reader can grade a number without leaving the page.
*/
const VITAL_HELP: Record<VitalName, string> = {
  lcp: 'Largest Contentful Paint — when the biggest thing on screen finished loading. Good is 2.5s or under; over 4s is poor. This is the one visitors mean by "slow".',
  inp: 'Interaction to Next Paint — how long the page takes to respond to a tap or click. Good is 200ms or under; over 500ms is poor. Only measured on visits where someone interacted, so its sample is smaller than LCP’s.',
  cls: 'Cumulative Layout Shift — how much the page jumps around while it loads. A score, not a time: good is 0.1 or under; over 0.25 is poor. 0 is perfect.',
  fcp: 'First Contentful Paint — when anything first appeared. Good is 1.8s or under; over 3s is poor.',
  ttfb: 'Time to First Byte — how long the server and network took before the browser had anything. Good is 800ms or under. Ours is well inside that, which is how we know the slowness is in the page, not the server.',
}

const GRADE_TONE: Record<'good' | 'needs-improvement' | 'poor', Tone> = {
  good: 'positive',
  'needs-improvement': 'caution',
  poor: 'negative',
}

const grade = (vital: VitalName, p75: number | null) => {
  if (p75 === null) return null
  const { good, poor } = THRESHOLDS[vital]
  if (p75 <= good) return 'good' as const
  return p75 <= poor ? ('needs-improvement' as const) : ('poor' as const)
}

/**
 * ⚠️ CLS is a unitless score and everything else is milliseconds, so they cannot share a
 * formatter. Milliseconds read as seconds past 1s, where "8.8s" is legible and "8800ms"
 * is arithmetic.
 */
const formatVital = (vital: VitalName, p75: number | null): string => {
  if (p75 === null) return '—'
  if (vital === 'cls') return p75.toFixed(p75 < 0.1 ? 3 : 2)
  return p75 >= 1_000 ? `${(p75 / 1_000).toFixed(2)}s` : `${Math.round(p75)}ms`
}

/**
 * One p75. Coloured by its grade; greyed and parenthesised when the API says the sample is
 * too small to carry it — the figure is still SHOWN, because hiding it would leave a blank
 * that reads as "fast", and the tooltip always says how many visits are behind it.
 */
const VitalCell = ({ vital, reading }: { vital: VitalName; reading: WebsiteVitalReading }) => {
  const tone = useTone()
  const g = grade(vital, reading.p75)
  const text = formatVital(vital, reading.p75)
  const weak = !reading.reliable
  const tip =
    reading.p75 === null
      ? 'No measured visits in this window.'
      : `${formatCount(reading.samples)} measured ${reading.samples === 1 ? 'visit' : 'visits'}${
          weak ? ' — too few to read as a percentile' : ''
        }. ${g === 'good' ? 'Within' : 'Outside'} Google’s good threshold of ${formatVital(
          vital,
          THRESHOLDS[vital].good,
        )}.`
  return (
    <InfoTooltip text={tip}>
      <Typography
        variant="body3"
        sx={{
          cursor: 'help',
          fontVariantNumeric: 'tabular-nums',
          color: weak || !g ? 'text.disabled' : tone(GRADE_TONE[g]).color,
          fontWeight: weak || !g ? 400 : 600,
        }}
      >
        {reading.p75 === null ? '—' : weak ? `(${text})` : text}
      </Typography>
    </InfoTooltip>
  )
}

const GroupCell = ({ row, byDevice }: { row: WebsiteVitalsRow; byDevice: boolean }) => (
  <Stack sx={{ minWidth: 0, py: 1 }}>
    <Typography variant="body3" noWrap sx={{ textTransform: byDevice ? 'capitalize' : 'none' }}>
      {row.group}
    </Typography>
    <Typography variant="body4" color="text.secondary" noWrap>
      {row.lcp.samples === 0
        ? 'No measured visits'
        : `${formatCount(row.lcp.samples)} measured ${row.lcp.samples === 1 ? 'visit' : 'visits'}`}
    </Typography>
  </Stack>
)

/*
  Widths sized for the HEADER, not the values (§49). These are MEASURED in the browser at
  1280 (`scrollWidth` vs `clientWidth` on each header's content box), not estimated from
  character counts — an earlier pass reasoned "a 4–5 character label plus ~63px of chrome"
  and shipped TTFB clipped, because 'TTFB' is a character wider than the three-letter
  labels and the (i) glyph is what fell off the end.

  Worst case per column, with the widest trend label selected:
    LCP 43 · INP 41 · CLS 42 · FCP 44 · TTFB 52 · 'TTFB trend' 88
  A vital column of 116 gives its header 53px of content box, and 124 gives the trend 92 —
  both clear their worst case with a few px spare. Header chrome (padding + the sort arrow)
  eats the remaining ~63px, which is why the column is so much wider than its label.

  Budget: 5 vitals at 116 plus a 124 trend is 704 of the 891px card body, leaving the group
  column 187 against its 180 minWidth — so nothing scrolls sideways (§74). That margin is
  thin: adding a sixth vital, or a longer trend label, needs the group minimum revisited
  rather than another few px shaved off here.
*/
const VITAL_COL_WIDTH = 116 - INFO_HEADER_ALLOWANCE

const buildColumns = (byDevice: boolean, trendVital: VitalName) => [
  {
    field: 'group',
    headerName: byDevice ? 'Device' : 'Page',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }: { row: WebsiteVitalsRow }) => (
      <GroupCell row={row} byDevice={byDevice} />
    ),
  },
  ...(Object.keys(VITAL_LABELS) as VitalName[]).map((vital) => ({
    field: vital,
    headerName: VITAL_LABELS[vital],
    width: VITAL_COL_WIDTH + INFO_HEADER_ALLOWANCE,
    type: 'number' as const,
    renderHeader: headerWithInfo(VITAL_LABELS[vital], VITAL_HELP[vital]),
    // Sorts on the figure, with "no data" last rather than first: a null sorted as 0
    // would put every unmeasured route at the top of an ascending sort as if it were
    // the fastest thing on the site.
    valueGetter: (_v: unknown, row: WebsiteVitalsRow) =>
      row[vital].p75 ?? Number.POSITIVE_INFINITY,
    renderCell: ({ row }: { row: WebsiteVitalsRow }) => (
      <VitalCell vital={vital} reading={row[vital]} />
    ),
  })),
  {
    field: 'trend',
    headerName: `${VITAL_LABELS[trendVital]} trend`,
    width: 124,
    sortable: false,
    renderHeader: headerWithInfo(
      `${VITAL_LABELS[trendVital]} trend`,
      `Daily p75 ${VITAL_LABELS[trendVital]} across the selected range, oldest on the left. A gap is a day with no measured visits, not a fast day. At this traffic a single day's p75 rests on a handful of visits, so read the shape over a week rather than any one point.`,
    ),
    renderCell: ({ row }: { row: WebsiteVitalsRow }) => (
      <Sparkline
        values={row.trend.map((p) => p.value)}
        width={92}
        label={`${VITAL_LABELS[trendVital]} trend for ${row.group}`}
      />
    ),
  },
]

const DIMENSION_OPTIONS: readonly ViewToggleOption<'route' | 'device'>[] = [
  { value: 'route', label: 'By page' },
  { value: 'device', label: 'By device' },
]
const TREND_OPTIONS: readonly ViewToggleOption<VitalName>[] = (
  Object.keys(VITAL_LABELS) as VitalName[]
).map((v) => ({ value: v, label: VITAL_LABELS[v] }))

// Measured at 1280 (the grid's two-line group row and its header).
const ROW_HEIGHT = 72
const HEADER_HEIGHT = 44
const MAX_BODY_ROWS = 8

export const WebsiteVitalsCard = ({ range }: { range: DayRange }) => {
  const [dimension, setDimension] = useState<'route' | 'device'>('route')
  const [trendVital, setTrendVital] = useState<VitalName>('lcp')
  const { data, isLoading, error } = useWebsiteVitals(range, trendVital)

  const rows = useMemo(
    () => (dimension === 'route' ? (data?.byRoute ?? []) : (data?.byDevice ?? [])),
    [data, dimension],
  )
  const columns = useMemo(
    () => buildColumns(dimension === 'device', trendVital),
    [dimension, trendVital],
  )
  const bodyRows = Math.max(3, Math.min(MAX_BODY_ROWS, rows.length))
  const height = HEADER_HEIGHT + ROW_HEIGHT * bodyRows + 2

  const vercelProblem =
    data && (data.sources.vercel.status === 'error' || data.sources.vercel.status === 'not_configured')
      ? sourceNotice(AUDIENCE_LABELS.all, data.sources.vercel)
      : null

  return (
    <Card
      title="Speed"
      action={
        <Stack direction="row" spacing={3} alignItems="center">
          <InfoTooltip text="Core Web Vitals as real visitors experienced them, from Vercel Speed Insights. Google grades a page on the 75th percentile, so that is what every figure here is — not an average, which would hide the slow tail that makes a page feel broken." />
          <ViewToggle
            options={DIMENSION_OPTIONS}
            value={dimension}
            onChange={setDimension}
            ariaLabel="Group vitals by page or device"
          />
        </Stack>
      }
    >
      <Stack spacing={3} sx={{ mt: 4 }} data-testid="website-vitals-card">
        <SectionNote>
          {AUDIENCE_LABELS.all} — Speed Insights is cookieless, so this is everyone, not only
          visitors with analytics on. Every figure is the 75th percentile, the threshold Google
          grades on.{' '}
          {data
            ? `The p75 columns cover the ${data.pooledWindow.days === 1 ? 'day' : `${data.pooledWindow.days} days`} ending ${shortDate(
                `${data.pooledWindow.until}T00:00:00.000Z`,
              )} — Vercel can only pool a 7-day window, so they do not follow the range above; the trend does. Figures from fewer than ${data.minSamples} visits are shown in brackets and grey: at this traffic they are the slowest of a handful of visits rather than a percentile.`
            : ''}
        </SectionNote>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
          <Typography variant="body4" color="text.secondary">
            Trend column
          </Typography>
          <ViewToggle
            options={TREND_OPTIONS}
            value={trendVital}
            onChange={setTrendVital}
            ariaLabel="Which vital the trend column charts"
          />
        </Stack>
        {vercelProblem ? (
          <Notice severity={data?.sources.vercel.status === 'error' ? 'error' : 'warning'}>
            {vercelProblem}
          </Notice>
        ) : null}
        {error ? (
          <Notice severity="error">
            Could not load Core Web Vitals. Reload the page, or check the API is running.
          </Notice>
        ) : (
          <Table
            columns={columns}
            rows={rows}
            getRowId={(row: WebsiteVitalsRow) => row.group}
            loading={isLoading}
            defaultSortModel={[{ field: 'lcp', sort: 'desc' }]}
            showActionLine={false}
            showSelection={false}
            showFooter={false}
            hideExport
            height={height}
            emptyState={
              <EmptyState
                title="No speed measurements in this window"
                text="Vercel Speed Insights reports a vital once a real visitor has loaded the page. Once vera.inc has visitors in the window, each page's p75 appears here."
                icon={<GaugeIcon strokeWidth={1} size={32} />}
              />
            }
          />
        )}
        <Box>
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Vercel keeps 30 days of Speed Insights on our plan, so this cannot reach further back
            than that.
          </Typography>
        </Box>
      </Stack>
    </Card>
  )
}
